import type { BackendProductUpsertOperation, ObpSection, Validations } from "@credit-acceptance/step6-bff-client";
import { BackendProductUpsertOperationNameEnum } from "@credit-acceptance/step6-bff-client";
import type { FieldPath } from "react-hook-form";

type ObpProductValues = Omit<BackendProductUpsertOperation, "sellingPrice" | "termMonths" | "termMiles" | "formId"> & {
  sellingPrice: number | null;
  termMonths: number | null;
  termMiles: number | null;
  formId: number | null;
};

export interface ObpFormValues {
  products: ObpProductValues[];
}

// eslint-disable-next-line unicorn/no-null -- RHF requires null for cleared numbers; undefined restores the default value.
const EMPTY_NUMBER = null;

/**
 * Maps the API date format and nullable numbers into controlled form values.
 * @param obp The dashboard OBP section.
 * @returns {ObpFormValues} The editable products.
 */
export function toObpFormValues(obp?: ObpSection): ObpFormValues {
  return {
    products: (obp?.products ?? []).map((product) => ({
      ...product,
      sellingPrice: product.sellingPrice ?? EMPTY_NUMBER,
      termMonths: product.termMonths ?? EMPTY_NUMBER,
      termMiles: product.termMiles ?? EMPTY_NUMBER,
      formId: product.formId ?? EMPTY_NUMBER,
      // The BFF uses MM-dd-yyyy; the shared date input speaks ISO.
      effectiveDate: product.effectiveDate?.replace(/^(\d{2})-(\d{2})-(\d{4})$/, "$3-$1-$2") ?? "",
    })),
  };
}

/**
 * Serializes product values without the response-only validations.
 * @param values The current OBP form values.
 * @returns {ObpSection} The OBP contribution to the v2 save request.
 */
export function toObpSaveRequest(values: ObpFormValues): ObpSection {
  return {
    products: values.products.map((product) => ({
      ...product,
      sellingPrice: product.sellingPrice ?? undefined,
      termMonths: product.termMonths ?? undefined,
      termMiles: product.termMiles ?? undefined,
      formId: product.formId ?? undefined,
      effectiveDate: product.effectiveDate?.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$2-$3-$1") || undefined,
    })),
  };
}

const FIELD_MAP = {
  vendorName: "vendorName",
  certificateNumber: "certificateNumber",
  sellingPrice: "sellingPrice",
  termMonths: "termMonths",
  termMiles: "termMiles",
  unlimitedMileage: "unlimitedMileage",
  effectiveDate: "effectiveDate",
  formId: "formNumber",
  formNumber: "formNumber",
  selectedProducts: "selectedProducts",
} as const;

/**
 * Routes both violation buckets by the BFF's context.productName discriminator.
 * @param validations The GET or PUT validations.
 * @param products The currently rendered products, in form order.
 * @returns {object} Field errors and fallback messages for violations without a visible field.
 */
export function translateObpViolations(
  validations: Validations | undefined,
  products: Array<Pick<BackendProductUpsertOperation, "name">>
) {
  const errors = new Map<FieldPath<ObpFormValues>, string>();
  const general: string[] = [];
  for (const violation of [
    ...(validations?.requiredViolations ?? []),
    ...(validations?.businessRuleViolations ?? []),
  ]) {
    const productName: unknown = violation.context?.productName;
    const index = products.findIndex((product) => product.name === productName);
    const field = Object.entries(FIELD_MAP).find(([key]) => key === violation.field)?.[1];
    const product = products[index];
    const isAppearance = product?.name === BackendProductUpsertOperationNameEnum.AppearanceProtection;
    const isBundle = product?.name === BackendProductUpsertOperationNameEnum.BundledProducts;
    const fieldVisible =
      field &&
      (field !== "selectedProducts" || isBundle) &&
      (field !== "unlimitedMileage" || !isBundle) &&
      (!isAppearance || ["vendorName", "sellingPrice", "effectiveDate", "formNumber"].includes(field));
    if (index < 0 || !fieldVisible) {
      general.push(typeof productName === "string" ? `${productName}: ${violation.message}` : violation.message);
      continue;
    }
    const path: FieldPath<ObpFormValues> = `products.${index}.${field}`;
    const previous = errors.get(path);
    errors.set(path, previous ? `${previous}\n${violation.message}` : violation.message);
  }
  return { errors, general };
}
